import moment from 'moment';

import { deepClone } from '@/utils/objects';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';
import { getRollingDataForInterval, portfolioHoldingsToComparisonHoldings } from '@/utils/portfolio/utils';
import { Interval } from '@/utils/constants';
import calculatePortfolioStats, { recalculatePortfolioAllocations } from '@/utils/portfolio/calculate';
import {
  calculateCalendarYearFromRolling,
  calculatePerformanceCommonInception,
  calculatePortfolioSinceInception,
  calculateTrailingReturns,
} from '@/utils/calculation-utils';
import { CURRENCY } from '@/utils/currency';
import { benchmarkIsFull, calculateBenchmarkPortfolioStats, comparisonBenchmarkToBenchmark } from '@/utils/comparison/benchmarks';
import { benchmarkToComparisonItem, fundToComparisonItem, stockToComparisonItem } from '@/utils/comparison';

import { Dispatch } from '../../index';
import { ReduxState } from '../types';
import { getPortfolioHoldingsData } from '../edit-portfolio';
import { selectCurrentTaxRate } from '../taxes/selectors';
import disclaimers from '../../../utils/disclaimers';
import { getBenchmarkPortfolio } from '../comparison-benchmarks/benchmark-portfolios';
import { getBenchmarksData } from '../common/actionCreators';
import ComparisonItem from '@/types/comparison-item';
import Portfolio from '@/types/portfolio';
import PortfolioOverrideDates from '@/types/portfolio-override-dates';
import Risk from '@/types/risk';
import CommonInception from '@/types/common-inception';
import Comparison from '@/types/comparison';
import { getFundData } from '../funds/fund-store';
import { getFundBenchmarks } from '../benchmarks/benchmark-store';
import { getStockData } from '../stocks/stock-store';
import { getStockBenchmarks } from '../comparison-benchmarks/comparison-benchmarks-store';
import AppRegion from '@/types/app-region';
import PercentGrowth from '@/types/percent-growth';
import { getMaxCommonInceptionDate } from '@/utils/common-inception';
import { PortfolioData } from '@/utils/portfolios';

export const recalcPortfolioItem = async (
  dispatch: Dispatch,
  state: ReduxState,
  item: ComparisonItem,
  forcedMonthlyRolling: boolean,
  useShort?: boolean,
  showBaseCurrency?: boolean,
  reallocate?: boolean,
  commonInception?: CommonInception,
  preFetchedData?: {
    portfolioData: PortfolioData;
    taxRate: number;
  },
) => {
  const {
    auth: { user },
  } = state;
  const clone = deepClone(item);
  if (!clone.source || !clone.source.holdings.length) {
    return Promise.resolve(item);
  }

  let portfolio: Portfolio = clone.source;
  const { funds, stocks, alts, benchmarks } = deepClone(preFetchedData?.portfolioData ?? (await dispatch(getPortfolioHoldingsData(portfolio))));

  if (reallocate) {
    portfolio = recalculatePortfolioAllocations(portfolio, [...funds, ...stocks, ...alts]);
  }

  const taxRate = preFetchedData?.taxRate ?? selectCurrentTaxRate(state, portfolio.contact);
  const validateIncomeStream = user != null && userHasPermission(user, PERMISSIONS.INCOME_STREAM);
  const inceptionDate = getMaxCommonInceptionDate([item], useShort) || undefined;
  let inceptionMoment = inceptionDate ? moment.utc(inceptionDate) : undefined;
  const newPortfolio = calculatePortfolioStats({
    portfolio,
    alts,
    funds,
    stocks,
    benchmarks,
    taxRate,
    showBaseCurrency: false,
    forcedMonthlyRolling,
    shortHistory: useShort,
    commonInception,
  });

  // Refresh disclaimers
  clone.disclaimers = disclaimers.getComparisonPortfolioDisclaimers(
    portfolio,
    { funds, stocks, alts, benchmarks },
    validateIncomeStream,
    useShort,
    commonInception,
  );
  if (newPortfolio.performance) {
    const risk = { ...newPortfolio.performance.risk };

    const fees = (portfolio.discretionaryMer || 0) * (1 + taxRate);
    const rollingData = getRollingDataForInterval(portfolio, item.holdings, Interval.Monthly, fees, useShort);

    let forcedRollings = rollingData.override ? rollingData.rolling : [];
    const portfolioOverrideDates = portfolio.override?.dates != null ? (portfolio.override.dates as PortfolioOverrideDates) : null;
    if (portfolioOverrideDates?.inceptionDate != null) {
      inceptionMoment = moment.utc(portfolioOverrideDates.inceptionDate);
      forcedRollings = forcedRollings.filter(rolling => rolling[0] > moment.utc(portfolioOverrideDates.inceptionDate).toDate().getTime());
    }
    let { percentGrowth } = newPortfolio.performance;
    if (forcedRollings.length > 0) {
      percentGrowth = calculateTrailingReturns(forcedRollings);
      if (commonInception) {
        percentGrowth.CI = calculatePerformanceCommonInception(forcedRollings, commonInception?.startTimestamp);
      }
    } else {
      const monthlyRolling = rollingData.rolling;
      percentGrowth.INC = calculatePortfolioSinceInception(monthlyRolling, clone.holdings, useShort);
      if (commonInception) {
        percentGrowth.CI = calculatePerformanceCommonInception(monthlyRolling, commonInception?.startTimestamp);
      }
    }

    const percentGrowthOverride = portfolio.override?.percentGrowth != null ? (portfolio.override.percentGrowth as Partial<Risk>) : {};
    percentGrowth = {
      ...percentGrowth,
      ...percentGrowthOverride,
    };

    clone.performance = {
      ...clone.performance,
      percentGrowth,
      calendarYear: forcedRollings.length > 0 ? calculateCalendarYearFromRolling(forcedRollings) : newPortfolio.performance.calendarYear,
      ...risk,
    };
  }

  if (portfolio.override != null && portfolio.override.dates != null && !portfolio.isPartiallyOverriden) {
    clone.dates.portfolioGrowth = { portfolio: (portfolio.override.dates as any).portfolioGrowth };
  } else {
    clone.dates.portfolioGrowth = newPortfolio.dates.growth;
  }
  clone.dates.portfolioAllocations = newPortfolio.dates.allocations;
  clone.dates.portfolioHoldings = newPortfolio.dates.holdings;
  clone.dates.risk = newPortfolio.dates.risk;
  clone.dates.portfolioFixedIncomeSector = newPortfolio.dates.fixedIncomeSector;
  clone.dates.portfolioInvestmentInfo = newPortfolio.dates.investmentInfo;
  clone.dates.portfolioCharacteristics = newPortfolio.dates.characteristics;
  clone.dates.portfolioYieldToMaturity = newPortfolio.dates.yieldToMaturity;
  clone.dates.savedAt = newPortfolio.dates.savedAt;

  if (inceptionMoment) {
    clone.dates.inception = inceptionMoment.toDate();
  }

  if (showBaseCurrency) {
    const holdings = [...funds, ...stocks].filter(f => portfolio.holdings.some(holding => holding.original.id === f._id));
    for (const holding of holdings) {
      if (holding.currency === CURRENCY.USD && holding.performance.rollingUSD && holding.performance.percentGrowthUSD) {
        holding.performance.rolling = holding.performance.rollingUSD;
        holding.performance.percentGrowth = holding.performance.percentGrowthUSD;
        holding.performance.calendarYear = holding.performance.calendarYearUSD;
      }
    }

    const basePortfolio = calculatePortfolioStats({
      portfolio,
      alts,
      funds,
      stocks,
      benchmarks,
      taxRate,
      showBaseCurrency: true,
      forcedMonthlyRolling,
      shortHistory: useShort,
      commonInception,
    });

    if (basePortfolio && basePortfolio.performance && newPortfolio && newPortfolio.performance) {
      const data = [...funds, ...stocks].filter(f => basePortfolio.holdings.some(holding => holding.original.id === f._id));
      const holdings = portfolioHoldingsToComparisonHoldings(basePortfolio.holdings, data);
      const fees = (basePortfolio.discretionaryMer || 0) * (1 + taxRate);
      const monthlyData = getRollingDataForInterval(basePortfolio, holdings, Interval.Monthly, fees);

      // we currently always use CAD risk metrics
      const risk = { ...basePortfolio.performance.risk };

      const percentGrowth: PercentGrowth = {
        ...basePortfolio.performance.percentGrowth,
        INC: calculatePortfolioSinceInception(monthlyData.rolling, clone.holdings, useShort),
      };

      if (commonInception) {
        percentGrowth.CI = calculatePerformanceCommonInception(monthlyData.rolling, commonInception?.startTimestamp);
      }

      clone.performanceBase = {
        ...clone.performance,
        ...clone.performanceBase,
        percentGrowth,
        calendarYear: basePortfolio.performance.calendarYear,
        ...risk,
      };

      clone.dates.portfolioGrowth = basePortfolio.dates.growth;
      clone.dates.portfolioAllocations = basePortfolio.dates.allocations;
      clone.dates.portfolioHoldings = basePortfolio.dates.holdings;
      clone.dates.risk = basePortfolio.dates.risk;
      clone.dates.portfolioFixedIncomeSector = basePortfolio.dates.fixedIncomeSector;
      clone.dates.portfolioInvestmentInfo = basePortfolio.dates.investmentInfo;
      clone.dates.portfolioCharacteristics = basePortfolio.dates.characteristics;
      clone.dates.portfolioYieldToMaturity = basePortfolio.dates.yieldToMaturity;
      clone.dates.savedAt = basePortfolio.dates.savedAt;
    }
  }

  clone.source = newPortfolio;
  return Promise.resolve(clone);
};

export const recalcBenchmarkItem = async (
  dispatch: Dispatch,
  item: ComparisonItem,
  forcedMonthlyRolling: boolean,
  comparison?: Comparison,
  useShort?: boolean,
) => {
  const benchmark = deepClone(item);
  const benchmarkPortfolio = await dispatch(getBenchmarkPortfolio(item));
  const benchmarks = deepClone(await dispatch(getBenchmarksData(benchmarkPortfolio)));

  if (benchmarkIsFull(benchmarkPortfolio.holdings)) {
    const newBenchmarkPortfolio = await calculateBenchmarkPortfolioStats(
      dispatch,
      benchmarkPortfolio,
      benchmarks,
      forcedMonthlyRolling,
      useShort,
      comparison?.commonInception,
    );
    const newBenchmark = benchmarkToComparisonItem(newBenchmarkPortfolio, benchmarks, comparison?.commonInception);
    if (newBenchmark && newBenchmark.performance) {
      benchmark.performance = { ...benchmark.performance, ...newBenchmark.performance };
    }
  }

  return Promise.resolve(benchmark);
};

export const recalcFundItemRiskMetrics = async (dispatch: Dispatch, item: ComparisonItem, comparison: Comparison, appRegion: AppRegion) => {
  if (!item._id) {
    return Promise.resolve(item);
  }

  let clone = deepClone(item);

  const fund = await dispatch(getFundData(clone._id, ['FUND_NOT_FOUND']));
  const benchmarks = await dispatch(getFundBenchmarks(fund));
  const options = {
    comparison,
  };

  clone = Object.assign(clone, fundToComparisonItem(fund, appRegion, benchmarks, options));
  return Promise.resolve(clone);
};

export const recalcStockItemRiskMetrics = async (dispatch: Dispatch, item: ComparisonItem, comparison: Comparison) => {
  if (!item._id) {
    return Promise.resolve(item);
  }

  let clone = deepClone(item);
  const stock = await dispatch(getStockData(clone._id, ['STOCK_NOT_FOUND']));
  const benchmarks = await dispatch(getStockBenchmarks(stock));
  const options = {
    comparison,
  };

  clone = Object.assign(clone, stockToComparisonItem(stock, benchmarks.map(comparisonBenchmarkToBenchmark), options));
  return Promise.resolve(clone);
};
